import { z } from 'zod';

import { SiteSchema } from './site.js';
import { ShiftbookResponsiblePartyReferenceSchema } from './shiftbook-entry.js';

type BufferLike = {
  from(
    value: string | Uint8Array,
    encoding?: string,
  ): {
    toString(encoding: string): string;
  };
};

function resolveBuffer(): BufferLike | undefined {
  return (globalThis as typeof globalThis & { Buffer?: BufferLike }).Buffer;
}

function encodeBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);

  if (typeof globalThis.btoa === 'function') {
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return globalThis.btoa(binary);
  }

  const buffer = resolveBuffer();
  if (buffer) {
    return buffer.from(bytes).toString('base64');
  }

  throw new Error('Base64 encoding is not available in this runtime');
}

function decodeBase64ToUtf8(value: string): string {
  if (typeof globalThis.atob === 'function') {
    const binary = globalThis.atob(value);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  const buffer = resolveBuffer();
  if (buffer) {
    return buffer.from(value, 'base64').toString('utf8');
  }

  throw new Error('Base64 decoding is not available in this runtime');
}

export const ShiftbookNotificationSourceEventTypeSchema = z.enum(['entry.assignment.changed']);
export type ShiftbookNotificationSourceEventType = z.infer<
  typeof ShiftbookNotificationSourceEventTypeSchema
>;

export const ShiftbookEntryAssignmentChangedEventPayloadSchema = z
  .object({
    site: SiteSchema,
    entryId: z.string().uuid(),
    entryTitle: z.string().trim().min(1),
    actorId: z.string().trim().min(1),
    actorDisplayName: z.string().trim().min(1),
    previousAssignedUserId: z.string().trim().min(1).nullable(),
    newAssignedUserId: z.string().trim().min(1).nullable(),
    // Optional so pre-cutover outbox rows (written before the responsible-party
    // fields existed) still parse at deploy time; the worker derives the legacy
    // user-assignment semantics from previous/newAssignedUserId when absent.
    previous: ShiftbookResponsiblePartyReferenceSchema.nullable().optional(),
    next: ShiftbookResponsiblePartyReferenceSchema.nullable().optional(),
    previousRecipientIds: z.array(z.string().trim().min(1)).optional(),
    nextRecipientIds: z.array(z.string().trim().min(1)).optional(),
  })
  .strict();
export type ShiftbookEntryAssignmentChangedEventPayload = z.infer<
  typeof ShiftbookEntryAssignmentChangedEventPayloadSchema
>;

export const ShiftbookEntryAssignmentChangedSourceEventSchema = z
  .object({
    eventType: z.literal('entry.assignment.changed'),
    payload: ShiftbookEntryAssignmentChangedEventPayloadSchema,
  })
  .strict();
export type ShiftbookEntryAssignmentChangedSourceEvent = z.infer<
  typeof ShiftbookEntryAssignmentChangedSourceEventSchema
>;

export const ShiftbookNotificationSourceEventSchema = z.discriminatedUnion('eventType', [
  ShiftbookEntryAssignmentChangedSourceEventSchema,
]);
export type ShiftbookNotificationSourceEvent = z.infer<
  typeof ShiftbookNotificationSourceEventSchema
>;

export const ShiftbookNotificationKindSchema = z.enum([
  'entry.assigned',
  'entry.assignment.removed',
]);
export type ShiftbookNotificationKind = z.infer<typeof ShiftbookNotificationKindSchema>;

export const ShiftbookEntryAssignedNotificationPayloadSchema = z
  .object({
    site: SiteSchema,
    entryId: z.string().uuid(),
    entryTitle: z.string().trim().min(1),
    actorDisplayName: z.string().trim().min(1),
    assignedUserId: z.string().trim().min(1),
    commentPreview: z.string().trim().min(1).optional(),
    dueAt: z.string().datetime().nullable().optional(),
  })
  .strict();
export type ShiftbookEntryAssignedNotificationPayload = z.infer<
  typeof ShiftbookEntryAssignedNotificationPayloadSchema
>;

export const ShiftbookEntryAssignmentRemovedNotificationPayloadSchema =
  ShiftbookEntryAssignedNotificationPayloadSchema;
export type ShiftbookEntryAssignmentRemovedNotificationPayload = z.infer<
  typeof ShiftbookEntryAssignmentRemovedNotificationPayloadSchema
>;

const ShiftbookNotificationRecordBaseSchema = z
  .object({
    id: z.string().uuid(),
    userId: z.string().trim().min(1),
    site: SiteSchema,
    isRead: z.boolean(),
    createdAt: z.string().datetime(),
  })
  .strict();

export const ShiftbookEntryAssignedNotificationRecordSchema =
  ShiftbookNotificationRecordBaseSchema.extend({
    kind: z.literal('entry.assigned'),
    payload: ShiftbookEntryAssignedNotificationPayloadSchema,
  }).strict();
export type ShiftbookEntryAssignedNotificationRecord = z.infer<
  typeof ShiftbookEntryAssignedNotificationRecordSchema
>;

export const ShiftbookEntryAssignmentRemovedNotificationRecordSchema =
  ShiftbookNotificationRecordBaseSchema.extend({
    kind: z.literal('entry.assignment.removed'),
    payload: ShiftbookEntryAssignmentRemovedNotificationPayloadSchema,
  }).strict();
export type ShiftbookEntryAssignmentRemovedNotificationRecord = z.infer<
  typeof ShiftbookEntryAssignmentRemovedNotificationRecordSchema
>;

export const ShiftbookNotificationRecordSchema = z.discriminatedUnion('kind', [
  ShiftbookEntryAssignedNotificationRecordSchema,
  ShiftbookEntryAssignmentRemovedNotificationRecordSchema,
]);
export type ShiftbookNotificationRecord = z.infer<typeof ShiftbookNotificationRecordSchema>;

export const ShiftbookNotificationCursorSchema = z
  .object({
    createdAt: z.string().datetime(),
    id: z.string().uuid(),
  })
  .strict();
export type ShiftbookNotificationCursor = z.infer<typeof ShiftbookNotificationCursorSchema>;

export const ShiftbookNotificationListQuerySchema = z
  .object({
    site: SiteSchema,
    limit: z.coerce.number().int().positive().max(100).default(20),
    cursor: z.string().min(1).optional(),
  })
  .strict();
export type ShiftbookNotificationListQuery = z.infer<typeof ShiftbookNotificationListQuerySchema>;

export const ShiftbookNotificationListResponseSchema = z
  .object({
    notifications: z.array(ShiftbookNotificationRecordSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type ShiftbookNotificationListResponse = z.infer<
  typeof ShiftbookNotificationListResponseSchema
>;

export const ShiftbookNotificationMarkReadRequestSchema = z
  .object({
    site: SiteSchema,
    notificationId: z.string().uuid(),
  })
  .strict();
export type ShiftbookNotificationMarkReadRequest = z.infer<
  typeof ShiftbookNotificationMarkReadRequestSchema
>;

export const ShiftbookNotificationMarkAllReadRequestSchema = z
  .object({
    site: SiteSchema,
  })
  .strict();
export type ShiftbookNotificationMarkAllReadRequest = z.infer<
  typeof ShiftbookNotificationMarkAllReadRequestSchema
>;

export const ShiftbookNotificationUnreadCountResponseSchema = z
  .object({
    unreadCount: z.number().int().min(0),
  })
  .strict();
export type ShiftbookNotificationUnreadCountResponse = z.infer<
  typeof ShiftbookNotificationUnreadCountResponseSchema
>;

export const ShiftbookNotificationRefreshEnvelopeSchema = z
  .object({
    type: z.literal('notification.changed'),
    site: SiteSchema,
    unreadCount: z.number().int().min(0).optional(),
  })
  .strict();
export type ShiftbookNotificationRefreshEnvelope = z.infer<
  typeof ShiftbookNotificationRefreshEnvelopeSchema
>;

export function encodeCursor(cursor: ShiftbookNotificationCursor): string {
  const parsedCursor = ShiftbookNotificationCursorSchema.parse(cursor);
  return encodeBase64(JSON.stringify(parsedCursor));
}

export function decodeCursor(cursor: string): ShiftbookNotificationCursor {
  try {
    const parsedCursor = JSON.parse(decodeBase64ToUtf8(cursor)) as unknown;
    return ShiftbookNotificationCursorSchema.parse(parsedCursor);
  } catch (error) {
    throw new Error('Invalid notification cursor', { cause: error });
  }
}
